import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, LinearProgress, Tabs, Tab, useTheme, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInsights } from '../actions/insights';
import { transformInsightsForUI } from '../utils/transformers';
import ExecutiveSummary from './sections/ExecutiveSummary';
import AnalysisSection from './sections/AnalysisSection';
import InsightsSection from './sections/InsightsSection';
import RecommendationsSection from './sections/RecommendationsSection';
import LoadingSkeleton from './LoadingSkeleton';
import { CircularProgress } from '@material-ui/core';
import { getInsights, getInsightsLoading, getInsightsError } from '../selectors/insights';
import { QUESTION_TYPES } from '../api/constants';
import DebugPanel from './DebugPanel';
import OpenEndedVisualization from './question-types/OpenEndedVisualization';
import BinaryVisualization from './question-types/BinaryVisualization';
import MultipleChoiceVisualization from './question-types/MultipleChoiceVisualization';
import ScaleVisualization from './question-types/ScaleVisualization';
import StackRankVisualization from './question-types/StackRankVisualization';
import MultipleRatingVisualization from './question-types/MultipleRatingVisualization';
import MatrixVisualization from './question-types/MatrixVisualization';
import { AIApi } from '../api/AIApi';
import EnhancedSentimentAnalysis from './EnhancedSentimentAnalysis';
// Import Icons
import InfoIcon from '@material-ui/icons/Info';
import BarChartIcon from '@material-ui/icons/BarChart';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HelpIcon from '@material-ui/icons/Help';
import RefreshIcon from '@material-ui/icons/Refresh';
import SyncProblemIcon from '@material-ui/icons/SyncProblem';
import TimerIcon from '@material-ui/icons/Timer';

// Import the common stats component
import CommonStatsDisplay from './CommonStatsDisplay';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  tabPanel: {
    height: '100%',
  },
  questionContainer: {
    marginBottom: theme.spacing(3),
  },
  questionHeader: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 500
  },
  subtitle: {
    color: theme.palette.text.secondary
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1]
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  markdown: {
    '& p': {
      marginBottom: theme.spacing(2)
    },
    '& ul': {
      paddingLeft: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    '& li': {
      marginBottom: theme.spacing(1)
    }
  },
  debugPanel: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    '& pre': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }
  },
  distribution: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  distributionItem: {
    marginBottom: theme.spacing(1),
    '& > *': {
      marginBottom: theme.spacing(0.5)
    }
  },
  metric: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& > *:first-child': {
      marginRight: theme.spacing(1)
    }
  },
  bullet: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& > *:first-child': {
      marginRight: theme.spacing(1)
    }
  },
  errorContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#FAFAFA',
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.shadows[1]
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main
  },
  errorTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 500
  },
  errorMessage: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  retryButton: {
    margin: theme.spacing(1),
  },
  progress: {
    height: 8,
    borderRadius: 4
  },
  metricValue: {
    minWidth: 50,
    textAlign: 'right'
  },
  sectionContent: {
    marginBottom: theme.spacing(2),
    lineHeight: '1.7',
    fontSize: '1rem',
    color: theme.palette.text.primary,
    whiteSpace: 'pre-line',
  },
  bulletPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2.5),
    position: 'relative',
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.shape.borderRadius,
    },
  },
  bulletIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    marginTop: '4px',
    fontSize: '0.8rem',
  },
  bulletContent: {
    flex: 1,
  },
  executiveSummary: {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    '& p': {
      marginTop: 0,
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1),
    },
  },
  recommendationItem: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    position: 'relative',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
  },
  importanceIndicator: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginRight: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    width: 'fit-content',
    opacity: 0.85,
    '&:hover': {
      opacity: 1,
    },
  },
  highImportance: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  mediumImportance: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  lowImportance: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  confidenceIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 0,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    verticalAlign: 'middle',
  },
  highConfidence: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  mediumConfidence: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  lowConfidence: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  }
}));

// TabPanel component
function TabPanel({ children, value, index, classes }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
    >
      {value === index && (
        <Box className={classes.tabPanel}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired
};

const MetricDisplay = ({ label, value, classes }) => (
  <Box className={classes.metric}>
    <Typography variant="body2">{label}</Typography>
    <Box flexGrow={1}>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        className={classes.progress}
      />
    </Box>
    <Typography variant="body2" className={classes.metricValue}>
      {value}%
    </Typography>
  </Box>
);

const DistributionSection = ({ distribution, questionType, classes }) => {
  if (!distribution || distribution.length === 0) return null;

  return (
    <Box className={classes.distribution}>
      <Typography variant="subtitle2" gutterBottom>
        Response Distribution
      </Typography>
      {distribution.map((item, index) => (
        <Box key={index} className={classes.distributionItem}>
          <MetricDisplay
            label={item.label}
            value={item.value}
            classes={classes}
          />
        </Box>
      ))}
    </Box>
  );
};

// Reusable Section Component
const Section = ({ icon, title, children, classes }) => (
  <Paper className={classes.section}>
    <Box className={classes.sectionHeader}>
      {icon}
      <Typography variant="h6" className={classes.sectionTitle}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

Section.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  classes: PropTypes.object.isRequired,
};

const AIInsightsPanelV2 = ({
  eventId,
  questionType,
  data,
  stats,
  onError,
  onLoadingComplete,
  debug = true
}) => {
  // Log all incoming props for debugging with detailed structure
  /* console.log('🔎 AIInsightsPanelV2 - Received props:', {
    eventId,
    questionType,
    dataAvailable: !!data,
    statsAvailable: !!stats,
    dataKeys: data ? Object.keys(data) : [],
    statsKeys: stats ? Object.keys(stats) : [],
    timestamp: new Date().toISOString()
  }); */
  
  // If this is a stack rank question, log the relevant data for comparison with extensive details
  if (questionType === 'stack_rank' && stats) {
    /* console.log('🏆 AIInsightsPanelV2 - Stack Rank data deep inspection:', {
      // Direct paths used in stackRankPrompt.js
      directStatsIdeas: {
        available: !!stats.ideas,
        count: stats.ideas?.length || 0,
        firstItem: stats.ideas?.[0] || 'No items'
      },
      summaryWinningIdeas: {
        available: !!stats.summary?.winning_ideas,
        count: stats.summary?.winning_ideas?.length || 0,
        firstItem: stats.summary?.winning_ideas?.[0] || 'No items'
      },
      eventIdeas: {
        available: !!stats.event?.ideas,
        count: stats.event?.ideas?.length || 0,
        firstItem: stats.event?.ideas?.[0] || 'No items'
      },
      dataIdeas: {
        available: !!stats.data?.ideas,
        count: stats.data?.ideas?.length || 0,
        firstItem: stats.data?.ideas?.[0] || 'No items'
      },
      resultsIdeas: {
        available: !!stats.results?.ideas,
        count: stats.results?.ideas?.length || 0,
        firstItem: stats.results?.ideas?.[0] || 'No items'
      },
      options: {
        available: !!stats.options,
        count: stats.options?.length || 0,
        firstItem: stats.options?.[0] || 'No items'
      },
      choices: {
        available: !!stats.choices,
        count: stats.choices?.length || 0,
        firstItem: stats.choices?.[0] || 'No items'
      },
      // Check other possible properties
      statsStructure: {
        hasRankings: !!stats.rankings,
        hasSummary: !!stats.summary,
        hasEvent: !!stats.event,
        hasData: !!stats.data,
        hasResults: !!stats.results,
        hasMetrics: !!stats.metrics,
        summaryKeys: stats.summary ? Object.keys(stats.summary) : [],
        metricsKeys: stats.metrics ? Object.keys(stats.metrics) : []
      },
      fullStats: stats, // Log entire stats object for inspection
      timestamp: new Date().toISOString()
    }); */
  }
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const [transformedInsights, setTransformedInsights] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  
  const insights = useSelector(state => getInsights(state, eventId));
  const loading = useSelector(state => getInsightsLoading(state, eventId));
  const error = useSelector(state => getInsightsError(state, eventId));

  // Enhanced loading step state
  const [loadingStep, setLoadingStep] = useState('initializing');

  useEffect(() => {
    if (loading) {
      // Simulate step progression for demo; in real app, this could be tied to actual async steps
      setLoadingStep('generating_prompt');
      const stepTimeouts = [
        setTimeout(() => setLoadingStep('generating_prompt'), 400),
        setTimeout(() => setLoadingStep('fetching_data'), 1200),
        setTimeout(() => setLoadingStep('analyzing_response'), 2200)
      ];
      return () => stepTimeouts.forEach(clearTimeout);
    } else {
      setLoadingStep('initializing');
    }
  }, [loading]);

  useEffect(() => {
    if (eventId) {
      // console.log('Fetching insights:', { eventId, questionType });
      dispatch(fetchInsights(eventId));
    }
  }, [eventId, questionType, dispatch]);

  useEffect(() => {
    if (insights) {
      // Process insights here
      try {
        const transformed = transformInsightsForUI(insights, questionType);
        setTransformedInsights(transformed);
      } catch (err) {
        if (onError) {
          onError(err);
        }
      }
    }
    
    /* Commented out verbose logging
    if (insights) {
      console.log('Processing insights', { 
        hasInsights: !!insights,
        content: insights?.content ? Object.keys(insights.content) : [],
        rawData: insights,
        timestamp: new Date().toISOString()
      });

      try {
        const transformed = transformInsightsForUI(insights, questionType);
        console.log('Transformed insights', {
          hasTransformed: !!transformed,
          transformedData: transformed,
          metrics: transformed?.metrics,
          timestamp: new Date().toISOString()
        });
      } catch (err) {
        console.error('Error transforming insights', {
          error: err,
          rawData: insights,
          timestamp: new Date().toISOString()
        });
      }
    }
    */
  }, [insights, questionType, onError]);

  useEffect(() => {
    if (error && onError) {
      console.error('Error in insights panel:', error);
      onError(error);
    }
  }, [error, onError]);

  useEffect(() => {
    if (!loading && onLoadingComplete) {
      // console.log('Loading complete');
      onLoadingComplete();
    }
  }, [loading, onLoadingComplete]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const renderContent = () => {
    // console.log('Rendering content with data:', transformedInsights);

    if (!transformedInsights || (!transformedInsights.executiveSummary && !transformedInsights.analysis)) {
      // console.log('No transformed insights available');
      return (
        <Typography variant="body1" color="textSecondary">
          No insights available
        </Typography>
      );
    }

    // Log the entire transformedInsights object for debugging
    // console.log('Transformed insights object:', transformedInsights);

    // Destructure the transformed insights while ensuring all sections are accessible
    const { 
      analysis: aiAnalysis, 
      executiveSummary: aiSummary, 
      recommendations: aiRecommendations, 
      limitations, 
      surveyDesignFeedback,
      metrics: allMetrics, 
      questionType: currentQuestionType 
    } = transformedInsights;
    
    // console.log('aiAnalysis:', aiAnalysis);
    // console.log('limitations from transform:', limitations?.content);
    // console.log('surveyDesignFeedback from transform:', surveyDesignFeedback?.content);
    // console.log('limitationsContent from extraction:', limitationsContent);
    // console.log('surveyDesignFeedbackContent from extraction:', surveyDesignFeedbackContent);
    
    // Direct extraction from raw LLM response data
    // We need to bypass the transformer which may not be correctly extracting these sections
    let limitationsContent = '';
    let surveyDesignFeedbackContent = '';
    
    // Extract raw LLM response, handling both string and object formats
    let rawResponse = '';
    
    // Try to extract from multiple possible paths
    const possiblePaths = [
      insights?.debugInfo?.response?.rawResponse,
      transformedInsights?.debugInfo?.response?.rawResponse,
      insights?.debugInfo?.rawResponse,
      transformedInsights?.debugInfo?.rawResponse,
      insights?.content?.rawResponse,
      transformedInsights?.content?.rawResponse,
      transformedInsights?.rawResponse,
      insights?.rawResponse
    ];
    
    // Find the first non-empty path
    for (const path of possiblePaths) {
      if (path) {
        // Handle both string and object formats
        if (typeof path === 'string') {
          rawResponse = path;
          // console.log('Found rawResponse as string, length:', rawResponse.length);
          break;
        } else if (typeof path === 'object') {
          // For object format, look for content/text property or stringify it
          if (path.content) {
            rawResponse = path.content;
            // console.log('Found rawResponse as object.content, length:', rawResponse.length);
            break;
          } else if (path.text) {
            rawResponse = path.text;
            // console.log('Found rawResponse as object.text, length:', rawResponse.length);
            break;
          } else {
            // Last resort: try to use the whole response content
            const contentStr = insights?.content || transformedInsights?.content;
            if (typeof contentStr === 'string' && contentStr.includes('#')) {
              rawResponse = contentStr;
              // console.log('Using complete content as rawResponse, length:', rawResponse.length);
              break;
            }
          }
        }
      }
    }
    
    // console.log('Final raw LLM response, length:', rawResponse?.length);
    
    // Also get limitations and survey feedback directly from transformed insights
    const transformedLimitations = transformedInsights?.limitations?.content;
    const transformedSurveyFeedback = transformedInsights?.surveyDesignFeedback?.content;
    
    // console.log('Transformed limitations:', transformedLimitations);
    // console.log('Transformed survey feedback:', transformedSurveyFeedback);
    
    // Always try to access the content from transformedInsights first
    if (limitations?.content && 
        typeof limitations.content === 'string' &&
        limitations.content !== 'No limitations identified for this event.') {
      limitationsContent = limitations.content;
      // console.log('Using limitations from transformer:', limitationsContent);
    }
    
    if (surveyDesignFeedback?.content &&
        typeof surveyDesignFeedback.content === 'string' &&
        surveyDesignFeedback.content !== 'No survey design feedback was generated for this event.') {
      surveyDesignFeedbackContent = surveyDesignFeedback.content;
      // console.log('Using surveyDesignFeedback from transformer:', surveyDesignFeedbackContent);
    }
    
    // Then try extracting from raw response as fallback
    if (rawResponse && rawResponse.length > 0) {
      // Extract Analysis section if needed
      const analysisRegex = /# Analysis([\s\S]*?)(?=\n# [A-Za-z]|$)/i;
      const analysisMatch = rawResponse.match(analysisRegex);
      if (analysisMatch && analysisMatch[1] && (!aiAnalysis?.content || aiAnalysis.content.length === 0)) {
        // Convert to bullet points array
        const analysisContent = analysisMatch[1].trim()
          .split('\n-')
          .map(item => item.trim())
          .filter(item => item.length > 0)
          .map(item => item.startsWith('-') ? item : `- ${item}`);
        
        // Update analysis content
        if (transformedInsights.analysis) {
          transformedInsights.analysis.content = analysisContent;
          aiAnalysis = transformedInsights.analysis;
        }
      }
      
      // Extract Limitations section with multiple patterns
      const limitationsPatterns = [
        /# Limitations([\s\S]*?)(?=\n# [A-Za-z]|$)/i,  // H1 format
        /##\s*Limitations([\s\S]*?)(?=\n##\s*[A-Za-z]|$)/i,  // H2 format
        /\*\*Limitations\*\*:?([\s\S]*?)(?=\n\*\*[A-Za-z]|$)/i,  // Bold format
        /Limitations:?([\s\S]*?)(?=\n[A-Za-z]+ *:|$)/i  // Text format
      ];
      
      // Try each pattern until match found
      for (const pattern of limitationsPatterns) {
        const match = rawResponse.match(pattern);
        if (match && match[1] && match[1].trim().length > 0) {
          limitationsContent = match[1].trim();
          break;
        }
      }
      
      // Extract Survey Design Feedback with multiple patterns
      const surveyPatterns = [
        /# Survey Design Feedback([\s\S]*?)(?=\n# [A-Za-z]|$)/i,  // H1 format
        /##\s*Survey Design Feedback([\s\S]*?)(?=\n##\s*[A-Za-z]|$)/i,  // H2 format
        /\*\*Survey Design Feedback\*\*:?([\s\S]*?)(?=\n\*\*[A-Za-z]|$)/i,  // Bold format
        /Survey Design Feedback:?([\s\S]*?)(?=\n[A-Za-z]+ *:|$)/i  // Text format
      ];
      
      // Try each pattern until match found
      for (const pattern of surveyPatterns) {
        const match = rawResponse.match(pattern);
        if (match && match[1] && match[1].trim().length > 0) {
          surveyDesignFeedbackContent = match[1].trim();
          break;
        }
      }
    } 
    // else {
      // console.log('No raw LLM response found, cannot extract sections directly');
    // }
    
    // We'll directly use the extracted content instead of creating wrapper objects

    return (
      <Box>
        {/* AI Generated Sections */}
        {aiSummary?.content && (
          <Section icon={<InfoIcon color="primary" />} title="Executive Summary" classes={classes}>
            <Box className={classes.executiveSummary}>
              {aiSummary.content.split('\n\n').map((paragraph, idx) => (
                <Typography key={idx} variant="body1" className={classes.sectionContent} paragraph>
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Section>
        )}


        {/* Key Themes Section */}
        {aiAnalysis?.themes && aiAnalysis.themes.length > 0 && (
          <Section icon={<EqualizerIcon color="primary" />} title="Key Themes" classes={classes}>
            {aiAnalysis.themes.map((theme, index) => (
              <Box key={index} className={classes.bulletPoint} style={{ alignItems: 'flex-start', textAlign: 'left' }}>
                <FiberManualRecordIcon className={classes.bulletIcon} />
                <Box className={classes.bulletContent} style={{ textAlign: 'left' }}>
                  <Typography variant="body1">
                    {theme.split('\n').map((line, lineIdx) => {
                      // Bold the part before the first colon, if present
                      const colonIdx = line.indexOf(':');
                      if (colonIdx > 0) {
                        const before = line.slice(0, colonIdx + 1); // include colon
                        const after = line.slice(colonIdx + 1);
                        return (
                          <React.Fragment key={lineIdx}>
                            <span style={{ fontWeight: 600 }}>{before}</span>{after}
                            {lineIdx < theme.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        );
                      } else {
                        return (
                          <React.Fragment key={lineIdx}>
                            {line}
                            {lineIdx < theme.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        );
                      }
                    })}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Section>
        )}

        {/* Recommendations Section */}
        {aiRecommendations?.content && aiRecommendations.content.length > 0 && (
          <Section icon={<AssignmentTurnedInIcon color="primary" />} title="Recommendations" classes={classes}>
            {/* Group recommendations by priority: High, Medium, Low */}
            {(() => {
              // Map priorities: 0 = High, 1 = Medium, 2 = Low
              const grouped = [[], [], []];
              aiRecommendations.content.forEach((item, index) => {
                grouped[index % 3].push(item);
              });
              const priorityLabels = ['High Priority', 'Medium Priority', 'Low Priority'];
              const priorityClasses = [classes.highImportance, classes.mediumImportance, classes.lowImportance];
              return grouped.map((group, priority) =>
                group.map((item, idx) => (
                  <Box key={`${priority}-${idx}`} className={classes.recommendationItem}>
                    <Box className={`${classes.importanceIndicator} ${priorityClasses[priority]}`}>
                      {priorityLabels[priority]}
                    </Box>
                    <Typography variant="body1" className={classes.bulletContent}>
                      {item.split('\n').map((line, lineIdx) => (
                        <React.Fragment key={lineIdx}>
                          {line}
                          {lineIdx < item.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </Typography>
                  </Box>
                ))
              );
            })()}
          </Section>
        )}

        {/* Limitations Section - improved bullet formatting */}
{(limitationsContent || transformedLimitations || transformedInsights.limitations?.content) && (
  <Section icon={<PriorityHighIcon color="secondary" />} title="Limitations" classes={classes}>
    {/* Try to parse as array for bullet points, fallback to string */}
    {(() => {
      // Try to get array form
      let items = [];
      let raw = transformedLimitations || limitationsContent || transformedInsights.limitations?.content || '';
      if (Array.isArray(raw)) {
        items = raw;
      } else if (typeof raw === 'string') {
        // Split on bullet points or newlines
        items = raw.split(/\n[-•*→▪○💡🔍📊\s]*|^[-•*→▪○💡🔍📊\s]+/gm).map(s => s.trim()).filter(Boolean);
      }
      // Remove default fallback
      items = items.filter(item => !/no limitations identified/i.test(item));
      if (items.length > 0) {
        return items.map((item, idx) => (
          <Box key={idx} className={classes.bulletPoint} style={{ alignItems: 'flex-start', textAlign: 'left' }}>
            <FiberManualRecordIcon className={classes.bulletIcon} />
            <Box className={classes.bulletContent} style={{ textAlign: 'left' }}>
              <Typography variant="body1">{item}</Typography>
            </Box>
          </Box>
        ));
      } else {
        return <div>No limitations identified for this event.</div>;
      }
    })()}
  </Section>
)}

        {/* Survey Design Feedback Section - improved bullet formatting */}
{(surveyDesignFeedbackContent || transformedSurveyFeedback || transformedInsights.surveyDesignFeedback?.content) && (
  <Section icon={<HelpIcon color="primary" />} title="Survey Design Feedback" classes={classes}>
    {(() => {
      let items = [];
      let raw = transformedSurveyFeedback || surveyDesignFeedbackContent || transformedInsights.surveyDesignFeedback?.content || '';
      if (Array.isArray(raw)) {
        items = raw;
      } else if (typeof raw === 'string') {
        items = raw.split(/\n[-•*→▪○💡🔍📊\s]*|^[-•*→▪○💡🔍📊\s]+/gm).map(s => s.trim()).filter(Boolean);
      }
      items = items.filter(item => !/no survey design feedback was generated/i.test(item));
      if (items.length > 0) {
        return items.map((item, idx) => (
          <Box key={idx} className={classes.bulletPoint} style={{ alignItems: 'flex-start', textAlign: 'left' }}>
            <FiberManualRecordIcon className={classes.bulletIcon} />
            <Box className={classes.bulletContent} style={{ textAlign: 'left' }}>
              <Typography variant="body1">{item}</Typography>
            </Box>
          </Box>
        ));
      } else {
        return <div>No survey design feedback was generated for this event.</div>;
      }
    })()}
  </Section>
)}


        {/* Enhanced Error handling */}
        {error && (
          <Box className={classes.errorContainer}>
            {/* Different icons for different error types */}
            {error.message && error.message.includes('429') ? (
              <TimerIcon className={classes.errorIcon} />
            ) : error.message && error.message.includes('timeout') ? (
              <SyncProblemIcon className={classes.errorIcon} />
            ) : (
              <PriorityHighIcon className={classes.errorIcon} />
            )}
            
            <Typography variant="h5" className={classes.errorTitle}>
              {error.message && error.message.includes('429') ? 
                'Rate Limit Exceeded' : 
                error.message && error.message.includes('timeout') ?
                'Request Timed Out' :
                'Error Loading AI Insights'}
            </Typography>
            
            <Typography variant="body1" className={classes.errorMessage}>
              {error.message && error.message.includes('429') ? 
                "We've hit OpenAI's rate limit. This happens when there are too many requests or the prompt is too large. We've optimized the data to prevent this in the future." : 
                error.message && error.message.includes('timeout') ?
                "The request took too long to complete. This might be due to high server load or complexity of the analysis." :
                `An error occurred while generating insights. ${error.message}`}
            </Typography>
            
            <Box>
              <Button 
                variant="contained" 
                color="primary"
                className={classes.retryButton}
                onClick={() => {
                  // console.log('🔄 Manually retrying insights for event:', eventId);
                  // Add small delay to ensure rate limit has time to reset
                  setTimeout(() => {
                    dispatch(fetchInsights(eventId, questionType));
                  }, 2000);
                }}
                startIcon={<RefreshIcon />}
              >
                Try Again
              </Button>
              
              {debug && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  className={classes.retryButton}
                  onClick={() => {
                    // console.log('🧹 Clearing cache for event:', eventId);
                    // Clear the cache for this event
                    AIApi.clearInsightsCache(eventId);
                    // Re-fetch insights
                    setTimeout(() => {
                      dispatch(fetchInsights(eventId, questionType));
                    }, 1000);
                  }}
                >
                  Clear Cache & Retry
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    // Enhanced loading UI
    let stepLabel = '';
    let stepDesc = '';
    let stepIcon = <CircularProgress size={28} />;
    switch (loadingStep) {
      case 'generating_prompt':
        stepLabel = 'Generating prompt...';
        stepDesc = 'Preparing the AI prompt based on event data.';
        break;
      case 'fetching_data':
        stepLabel = 'Fetching data...';
        stepDesc = 'Retrieving responses and statistics from the server.';
        break;
      case 'analyzing_response':
        stepLabel = 'Analyzing responses...';
        stepDesc = 'Parsing and summarizing insights. This may take a few seconds.';
        break;
      default:
        stepLabel = 'Initializing...';
        stepDesc = 'Setting up AI insights.';
        break;
    }
    return (
      <Box className={classes.loadingContainer}>
        <Paper elevation={3} style={{ padding: 32, minWidth: 350, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {stepIcon}
          <Typography variant="h6" style={{ marginTop: 16 }}>{stepLabel}</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>{stepDesc}</Typography>
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">

            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!transformedInsights) {
    return (
      <Box className={classes.section}>
        <Typography>No insights available yet.</Typography>
      </Box>
    );
  }

  /* console.log('Rendering insights panel:', {
    hasTransformedInsights: !!transformedInsights,
    sections: Object.keys(transformedInsights),
    timestamp: new Date().toISOString()
  }); */

  return (
    <Box className={classes.root}>
      {/* Header section containing the AI Insights title */}
      {/* 
      <Box className={classes.header}>
        <Typography variant="h5" gutterBottom>
          AI Insights
        </Typography>
      </Box>
      */}

      <Box className={classes.content}>
        {renderContent()}
      </Box>

      {/* Debug Panel - shown in development or when debug is true */}
      {(process.env.NODE_ENV === 'development' || debug) && (
        <DebugPanel debugInfo={{
          sessionId: insights?.debugInfo?.sessionId,
          eventId,
          questionType: transformedInsights?.questionType,
          prompt: insights?.debugInfo?.prompt,
          response: insights?.debugInfo?.response,
          parsing: insights?.debugInfo?.parsing,
          cache: {
            hit: (insights?.hit !== undefined ? insights?.hit : insights?.fromCache) ? true : false,
            fromCache: insights?.fromCache,
            timestamp: insights?.metadata?.timestamp || (insights?.debugInfo?.cache && insights.debugInfo.cache.timestamp)
          },
          refreshControls: (
            <Box mt={2} display="flex" justifyContent="flex-start">
              <Button 
                variant="contained" 
                color="secondary" 
                size="small"
                onClick={() => {
                  // console.log('🔄 Manually refreshing insights for event:', eventId);
                  // Clear the cache for this event
                  AIApi.clearInsightsCache(eventId);
                  // Re-fetch insights
                  dispatch(fetchInsights(eventId, questionType));
                }}
              >
                Clear Cache & Refresh
              </Button>
            </Box>
          )
        }} />
      )}
    </Box>
  );
};

AIInsightsPanelV2.propTypes = {
  eventId: PropTypes.string.isRequired,
  questionType: PropTypes.string.isRequired,
  onError: PropTypes.func,
  onLoadingComplete: PropTypes.func,
  debug: PropTypes.bool
};

export default AIInsightsPanelV2; 